import { cva } from "class-variance-authority";
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  type CSSProperties,
  type ElementType,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/components/ui/utils";

/**
 * The app's typography levels in Tailwind's scale - shadcn has no Typography
 * component of its own; this one is the app's, kept slim. A level is its size,
 * line, weight and colour; anything more is a class at the call site.
 *
 * Each weight is the one Joy's face for the level drew: words in Inter's
 * Medium (500, the page's own), titles and ``body-xs`` in its SemiBold (600),
 * ``h1`` in its Bold; JetBrains Mono's numbers in its Regular (400) and its
 * titles in its Medium, which asked 600 the browser drew bold.
 */
const typographyVariants = cva("", {
  variants: {
    level: {
      h1: "text-7xl leading-tight font-bold tracking-tight text-foreground",
      h2: "text-3xl leading-tight font-semibold tracking-tight text-foreground",
      h3: "text-2xl leading-tight font-semibold tracking-tight text-foreground",
      h4: "text-xl leading-snug font-medium tracking-tight text-foreground",
      "title-lg": "text-lg leading-tight font-semibold text-foreground",
      "title-md": "text-sm leading-snug font-semibold text-foreground",
      "title-sm": "text-xs leading-snug font-semibold text-foreground",
      "body-lg": "text-lg leading-snug font-medium text-foreground",
      "body-md": "text-sm leading-snug font-medium text-foreground",
      "body-sm": "text-xs leading-snug font-medium text-foreground",
      "body-xs":
        "text-[11px] leading-snug font-semibold text-muted-foreground/80",
      "mono-h1": "font-mono text-3xl leading-relaxed font-bold text-foreground",
      "mono-h2": "font-mono text-2xl leading-relaxed font-bold text-foreground",
      "mono-h3": "font-mono text-xl leading-relaxed font-bold text-foreground",
      "mono-title-xl": "font-mono text-2xl leading-relaxed font-bold",
      "mono-title-lg": "font-mono text-xl leading-relaxed font-bold",
      "mono-title-md": "font-mono text-sm leading-snug font-medium",
      "mono-title-sm": "font-mono text-xs leading-snug font-medium",
      "mono-body-xl": "font-mono text-xl leading-normal font-normal",
      "mono-body-lg": "font-mono text-lg leading-normal font-normal",
      "mono-body-md": "font-mono text-sm leading-snug font-normal",
      "mono-body-sm": "font-mono text-xs leading-snug font-normal",
      inherit: "",
    },
  },
});

type Level = NonNullable<Parameters<typeof typographyVariants>[0]>["level"] &
  string;

const sans = "var(--font-sans)";
const mono = "var(--font-mono)";
const ink = "var(--foreground)";
/**
 * The same levels as style values, for what draws without classes - a
 * chart's labels, a style a field works out - kept beside the classes so the
 * two say one thing.
 */
const typographyStyles: Record<Level, CSSProperties> = {
  h1: {
    fontFamily: sans,
    fontSize: "4.5rem",
    lineHeight: 1.25,
    fontWeight: 700,
    letterSpacing: "-0.025em",
    color: ink,
  },
  h2: {
    fontFamily: sans,
    fontSize: "1.875rem",
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: "-0.025em",
    color: ink,
  },
  h3: {
    fontFamily: sans,
    fontSize: "1.5rem",
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: "-0.025em",
    color: ink,
  },
  h4: {
    fontFamily: sans,
    fontSize: "1.25rem",
    lineHeight: 1.375,
    fontWeight: 500,
    letterSpacing: "-0.025em",
    color: ink,
  },
  "title-lg": {
    fontFamily: sans,
    fontSize: "1.125rem",
    lineHeight: 1.25,
    fontWeight: 600,
    color: ink,
  },
  "title-md": {
    fontFamily: sans,
    fontSize: "0.875rem",
    lineHeight: 1.375,
    fontWeight: 600,
    color: ink,
  },
  "title-sm": {
    fontFamily: sans,
    fontSize: "0.75rem",
    lineHeight: 1.375,
    fontWeight: 600,
    color: ink,
  },
  "body-lg": {
    fontFamily: sans,
    fontSize: "1.125rem",
    lineHeight: 1.375,
    fontWeight: 500,
    color: ink,
  },
  "body-md": {
    fontFamily: sans,
    fontSize: "0.875rem",
    lineHeight: 1.375,
    fontWeight: 500,
    color: ink,
  },
  "body-sm": {
    fontFamily: sans,
    fontSize: "0.75rem",
    lineHeight: 1.375,
    fontWeight: 500,
    color: ink,
  },
  "body-xs": {
    fontFamily: sans,
    fontSize: "11px",
    lineHeight: 1.375,
    fontWeight: 600,
    color: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
  },
  "mono-h1": {
    fontFamily: mono,
    fontSize: "1.875rem",
    lineHeight: 1.625,
    fontWeight: 700,
    color: ink,
  },
  "mono-h2": {
    fontFamily: mono,
    fontSize: "1.5rem",
    lineHeight: 1.625,
    fontWeight: 700,
    color: ink,
  },
  "mono-h3": {
    fontFamily: mono,
    fontSize: "1.25rem",
    lineHeight: 1.625,
    fontWeight: 700,
    color: ink,
  },
  "mono-title-xl": {
    fontFamily: mono,
    fontSize: "1.5rem",
    lineHeight: 1.625,
    fontWeight: 700,
  },
  "mono-title-lg": {
    fontFamily: mono,
    fontSize: "1.25rem",
    lineHeight: 1.625,
    fontWeight: 700,
  },
  "mono-title-md": {
    fontFamily: mono,
    fontSize: "0.875rem",
    lineHeight: 1.375,
    fontWeight: 500,
  },
  "mono-title-sm": {
    fontFamily: mono,
    fontSize: "0.75rem",
    lineHeight: 1.375,
    fontWeight: 500,
  },
  "mono-body-xl": {
    fontFamily: mono,
    fontSize: "1.25rem",
    lineHeight: 1.5,
    fontWeight: 400,
  },
  "mono-body-lg": {
    fontFamily: mono,
    fontSize: "1.125rem",
    lineHeight: 1.5,
    fontWeight: 400,
  },
  "mono-body-md": {
    fontFamily: mono,
    fontSize: "0.875rem",
    lineHeight: 1.375,
    fontWeight: 400,
  },
  "mono-body-sm": {
    fontFamily: mono,
    fontSize: "0.75rem",
    lineHeight: 1.375,
    fontWeight: 400,
  },
  inherit: {},
};

/** The element each level is, standing on its own. */
const ELEMENTS: Partial<Record<Level, ElementType>> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  "title-lg": "p",
  "title-md": "p",
  "title-sm": "p",
  "body-lg": "p",
  "body-md": "p",
  "body-sm": "p",
  inherit: "p",
};

/** Words inside words: a Typography in another is a span of its line, at its level. */
const TypographyNestedContext = createContext(false);

/**
 * Words at one of the levels: the element the level is, its letters, colour
 * and line - or, inside other words, a span of their line at their level. A
 * skeleton it holds stands for its words, over each line.
 */
function Typography({
  level: levelProp,
  component,
  className,
  children,
  ...props
}: {
  level?: Level;
  /** Another element to draw it as. */
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  ref?: Ref<HTMLElement>;
} & Omit<React.AllHTMLAttributes<HTMLElement>, "color"> &
  Record<`data-${string}`, unknown>) {
  const nesting = useContext(TypographyNestedContext);
  const level: Level = levelProp ?? (nesting ? "inherit" : "body-md");
  const Element = component ?? (nesting ? "span" : (ELEMENTS[level] ?? "span"));
  const skeleton =
    Children.count(children) === 1 &&
    isValidElement(children) &&
    children.type === Skeleton
      ? (children as ReactElement<{ variant?: string }>)
      : null;
  return (
    <TypographyNestedContext.Provider value>
      <Element
        data-slot="typography"
        data-level={level}
        className={cn(
          nesting ? "inline" : "block",
          typographyVariants({ level }),
          !nesting && skeleton && "relative",
          className,
        )}
        {...props}
      >
        {skeleton
          ? cloneElement(skeleton, {
              variant: skeleton.props.variant || "inline",
            })
          : children}
      </Element>
    </TypographyNestedContext.Provider>
  );
}

export {
  Typography,
  TypographyNestedContext,
  typographyStyles,
  typographyVariants,
};
